import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  output,
  viewChild,
  type AfterViewInit,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
} from '@angular/core';

@Component({
  selector: 'ui-search-field',
  imports: [],
  templateUrl: './ui-search-field.component.html',
  styleUrl: './ui-search-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiSearchFieldComponent implements AfterViewInit {
  private static nextId = 0;

  private readonly searchInput = viewChild.required<ElementRef<HTMLInputElement>>('searchInput');

  public readonly id: InputSignal<string | null> = input<string | null>(null);
  public readonly label: InputSignal<string | null> = input<string | null>(null);
  public readonly value: InputSignal<string> = input<string>('');
  public readonly placeholder: InputSignal<string> = input<string>('Search');
  public readonly ariaLabel: InputSignal<string | null> = input<string | null>(null);
  public readonly disabled: InputSignal<boolean> = input<boolean>(false);
  public readonly autoFocus: InputSignal<boolean> = input<boolean>(false);
  public readonly clearLabel: InputSignal<string> = input<string>('Clear search');
  public readonly submitLabel: InputSignal<string> = input<string>('Submit search');
  public readonly valueChange: OutputEmitterRef<string> = output<string>();
  public readonly submitted: OutputEmitterRef<string> = output<string>();
  public readonly cleared: OutputEmitterRef<void> = output<void>();
  public readonly blurred: OutputEmitterRef<void> = output<void>();

  protected readonly fallbackId: string = `ui-search-field-${(UiSearchFieldComponent.nextId += 1)}`;
  protected readonly inputId: Signal<string> = computed((): string => this.id() ?? this.fallbackId);
  protected readonly hasLabel: Signal<boolean> = computed((): boolean => !!this.label());
  protected readonly hasValue: Signal<boolean> = computed((): boolean => this.value().length > 0);
  protected readonly inputAriaLabel: Signal<string | null> = computed((): string | null => {
    if (this.hasLabel()) {
      return null;
    }

    return this.ariaLabel() ?? this.placeholder();
  });

  public ngAfterViewInit(): void {
    this.focusInputIfRequested();
  }

  protected handleInput(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;

    this.valueChange.emit(target.value);
  }

  protected handleBlur(): void {
    this.blurred.emit();
  }

  protected handleClear(event: MouseEvent): void {
    if (this.disabled()) {
      event.preventDefault();
      return;
    }

    this.valueChange.emit('');
    this.cleared.emit();
  }

  protected handleSubmit(event: Event): void {
    event.preventDefault();

    if (this.disabled()) {
      return;
    }

    this.submitted.emit(this.value());
  }

  private focusInputIfRequested(): void {
    if (!this.autoFocus() || this.disabled()) {
      return;
    }

    this.searchInput().nativeElement.focus({ preventScroll: true });
  }
}
